import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import type { TicketTagDefinition } from '../../types/tickets';

type Props = {
  tags: TicketTagDefinition[];
  selectedIds: string[];
  disabled?: boolean;
  /** Hide the "Tags" label above the control. */
  hideLabel?: boolean;
  onChange: (nextIds: string[]) => void;
  onCreateTag?: (name: string) => Promise<TicketTagDefinition | null>;
};

export function TicketTagChips({
  tags,
  selectedIds,
  disabled,
  hideLabel,
  onChange,
  onCreateTag,
}: Props) {
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const updateMenuPosition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.max(rect.width, 200);
    const left = Math.min(rect.left, window.innerWidth - width - 8);
    setMenuStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: Math.max(8, left),
      width,
      zIndex: 4200,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
  }, [open, updateMenuPosition, tags.length, selectedIds.length]);

  useEffect(() => {
    if (!open) return undefined;
    function onDoc(ev: MouseEvent) {
      const target = ev.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKey(ev: KeyboardEvent) {
      if (ev.key === 'Escape') setOpen(false);
    }
    function onReposition() {
      updateMenuPosition();
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, updateMenuPosition]);

  const selected = tags.filter((t) => selectedIds.includes(t.id));
  const label =
    selected.length === 0
      ? 'No tags'
      : selected.length <= 2
        ? selected.map((t) => t.name).join(', ')
        : `${selected.length} tags selected`;

  function toggle(tagId: string) {
    if (disabled) return;
    const set = new Set(selectedIds);
    if (set.has(tagId)) set.delete(tagId);
    else set.add(tagId);
    onChange([...set]);
  }

  async function handleCreate() {
    const name = newName.trim();
    if (!name || !onCreateTag || creating) return;
    setCreating(true);
    try {
      const created = await onCreateTag(name);
      if (created) {
        onChange([...new Set([...selectedIds, created.id])]);
        setNewName('');
      }
    } finally {
      setCreating(false);
    }
  }

  const menu =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={menuRef}
            className="ticket-tag-select__menu ticket-tag-select__menu--portal"
            role="listbox"
            aria-multiselectable="true"
            style={menuStyle}
          >
            {tags.length === 0 ? (
              <p className="ticket-tag-select__empty">No tags yet. Create one below.</p>
            ) : null}
            {tags.map((tag) => {
              const active = selectedIds.includes(tag.id);
              return (
                <label key={tag.id} className="ticket-tag-select__option">
                  <input
                    type="checkbox"
                    checked={active}
                    disabled={disabled}
                    onChange={() => toggle(tag.id)}
                  />
                  <span
                    className="ticket-tag-select__swatch"
                    style={{ background: tag.color }}
                    aria-hidden
                  />
                  <span>{tag.name}</span>
                </label>
              );
            })}
            {onCreateTag ? (
              <div className="ticket-tag-select__create">
                <input
                  className="field-input field-input--compact"
                  value={newName}
                  disabled={disabled || creating}
                  placeholder="New tag name"
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleCreate();
                    }
                  }}
                />
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={disabled || creating || !newName.trim()}
                  onClick={() => void handleCreate()}
                >
                  {creating ? 'Adding…' : 'Add'}
                </button>
              </div>
            ) : null}
          </div>,
          document.body,
        )
      : null;

  return (
    <div className={`ticket-tag-select${hideLabel ? ' ticket-tag-select--no-label' : ''}`} ref={rootRef}>
      {hideLabel ? null : <span className="ticket-tag-select__label">Tags</span>}
      <button
        ref={triggerRef}
        type="button"
        className="ticket-tag-select__trigger field-input field-input--compact"
        disabled={disabled}
        aria-label="Tags"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="ticket-tag-select__value">{label}</span>
        <span className="ticket-tag-select__caret" aria-hidden>
          ▾
        </span>
      </button>
      {menu}
      {selected.length > 0 ? (
        <div className="ticket-tag-select__chips">
          {selected.map((tag) => (
            <span
              key={tag.id}
              className="ticket-tag-select__chip"
              style={{ background: `${tag.color}18`, color: tag.color, borderColor: `${tag.color}40` }}
            >
              {tag.name}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
